import { useState } from 'react'
import { toast } from 'sonner'
import kitApi, { ParsedRubricSection, SaveRubricRes } from '@/api/kit/KitApi'
import Button from '@/components/Button'
import Modal from '@/components/Modal'

type RubricImportStep = 'paste' | 'parsing' | 'review' | 'saving'

type RubricImportModalProps = {
    screeningJobId: string
    jobTitle: string
    hasExistingRubric: boolean
    onClose: () => void
    onSaved: (result: SaveRubricRes) => void
}

const SECTION_TYPE_COLORS: Record<string, string> = {
    KNOCKOUT: 'bg-red-100 text-red-700',
    BASICS: 'bg-blue-100 text-blue-700',
    EXPERIENCE: 'bg-purple-100 text-purple-700',
    BEHAVIOURS: 'bg-amber-100 text-amber-700',
}

const MIN_RUBRIC_LENGTH = 50
const MAX_RUBRIC_LENGTH = 50_000

function renderCriterionScores(
    sectionType: string,
    criterion: {
        passDescription: string | null
        flagDescription: string | null
        failDescription: string | null
        score1Description: string | null
        score2Description: string | null
        score3Description: string | null
    },
): React.ReactNode {
    if (sectionType === 'KNOCKOUT') {
        return null
    }

    if (sectionType === 'BASICS') {
        return (
            <ul className="ml-4 mt-1 space-y-0.5 text-xs text-gray-600">
                {criterion.passDescription && (
                    <li>
                        <span className="font-medium text-green-700">
                            Pass:
                        </span>{' '}
                        {criterion.passDescription}
                    </li>
                )}
                {criterion.flagDescription && (
                    <li>
                        <span className="font-medium text-yellow-700">
                            Flag:
                        </span>{' '}
                        {criterion.flagDescription}
                    </li>
                )}
                {criterion.failDescription && (
                    <li>
                        <span className="font-medium text-red-700">Fail:</span>{' '}
                        {criterion.failDescription}
                    </li>
                )}
            </ul>
        )
    }

    // EXPERIENCE and BEHAVIOURS
    return (
        <ul className="ml-4 mt-1 space-y-0.5 text-xs text-gray-600">
            {criterion.score1Description && (
                <li>
                    <span className="font-medium">1:</span>{' '}
                    {criterion.score1Description}
                </li>
            )}
            {criterion.score2Description && (
                <li>
                    <span className="font-medium">2:</span>{' '}
                    {criterion.score2Description}
                </li>
            )}
            {criterion.score3Description && (
                <li>
                    <span className="font-medium">3:</span>{' '}
                    {criterion.score3Description}
                </li>
            )}
        </ul>
    )
}

export default function RubricImportModal({
    screeningJobId,
    jobTitle,
    hasExistingRubric,
    onClose,
    onSaved,
}: RubricImportModalProps): React.JSX.Element {
    const [step, setStep] = useState<RubricImportStep>('paste')
    const [rubricText, setRubricText] = useState('')
    const [parsedSections, setParsedSections] = useState<
        ParsedRubricSection[] | null
    >(null)
    const [error, setError] = useState<string | null>(null)

    const isTextValid =
        rubricText.trim().length >= MIN_RUBRIC_LENGTH &&
        rubricText.length <= MAX_RUBRIC_LENGTH

    const handleParse = async () => {
        setError(null)
        setStep('parsing')
        try {
            const res = await kitApi.parseRubric({
                screeningJobId,
                rubricText: rubricText.trim(),
            })
            setParsedSections(res.sections)
            setStep('review')
        } catch (err) {
            console.error('Failed to parse rubric', err)
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to parse rubric. Please try again.',
            )
            setStep('paste')
        }
    }

    const handleConfirm = async () => {
        if (!parsedSections) return
        setError(null)
        setStep('saving')
        try {
            const result = await kitApi.saveRubric({
                screeningJobId,
                sections: parsedSections,
            })
            toast.success('Rubric saved successfully')
            onSaved(result)
            onClose()
        } catch (err) {
            console.error('Failed to save rubric', err)
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to save rubric. Please try again.',
            )
            setStep('review')
        }
    }

    const handleReject = () => {
        setParsedSections(null)
        setError(null)
        setStep('paste')
    }

    const modalSize = step === 'review' || step === 'saving' ? 'xl' : 'large'
    const allowClose = step !== 'parsing' && step !== 'saving'

    return (
        <Modal
            title={`Import Rubric - ${jobTitle}`}
            onClose={onClose}
            size={modalSize}
            allowUserToClose={allowClose}
        >
            <div className="flex flex-col gap-4">
                {error && (
                    <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {hasExistingRubric &&
                    (step === 'paste' || step === 'parsing') && (
                        <div className="rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
                            This screener already has a rubric. Re-importing
                            will replace it and delete all existing scores.
                        </div>
                    )}

                {(step === 'paste' || step === 'parsing') && (
                    <>
                        <textarea
                            className="form-input w-full font-mono text-sm"
                            rows={12}
                            maxLength={MAX_RUBRIC_LENGTH}
                            placeholder="Paste the full rubric document here..."
                            value={rubricText}
                            onChange={(e) => setRubricText(e.target.value)}
                            readOnly={step === 'parsing'}
                            disabled={step === 'parsing'}
                        />
                        <p className="text-xs text-gray-500">
                            Paste the full rubric document (minimum 50
                            characters)
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button
                                type="button"
                                style="secondary"
                                onClick={onClose}
                                enabled={step !== 'parsing' || true}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="primary"
                                onClick={() => void handleParse()}
                                enabled={isTextValid && step !== 'parsing'}
                            >
                                {step === 'parsing'
                                    ? 'Parsing...'
                                    : 'Parse Rubric'}
                            </Button>
                        </div>
                    </>
                )}

                {(step === 'review' || step === 'saving') && parsedSections && (
                    <>
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <div>
                                <h4 className="mb-2 text-sm font-semibold text-gray-700">
                                    Original Text
                                </h4>
                                <div className="max-h-[500px] overflow-y-auto whitespace-pre-wrap rounded border bg-gray-50 p-3 text-sm">
                                    {rubricText}
                                </div>
                            </div>
                            <div>
                                <h4 className="mb-2 text-sm font-semibold text-gray-700">
                                    Parsed Rubric
                                </h4>
                                <div className="max-h-[500px] overflow-y-auto rounded border bg-white p-3">
                                    {parsedSections.map((section, sIdx) => (
                                        <div
                                            key={sIdx}
                                            className="mb-4 last:mb-0"
                                        >
                                            <div className="mb-1 flex items-center gap-2">
                                                <span
                                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${SECTION_TYPE_COLORS[section.sectionType] ?? 'bg-gray-100 text-gray-700'}`}
                                                >
                                                    {section.sectionType}
                                                </span>
                                                <span className="text-sm font-medium text-gray-800">
                                                    {section.label}
                                                </span>
                                            </div>
                                            <ol className="ml-4 list-decimal space-y-1">
                                                {section.criteria.map(
                                                    (criterion, cIdx) => (
                                                        <li
                                                            key={cIdx}
                                                            className="text-sm text-gray-700"
                                                        >
                                                            {criterion.label}
                                                            {renderCriterionScores(
                                                                section.sectionType,
                                                                criterion,
                                                            )}
                                                        </li>
                                                    ),
                                                )}
                                            </ol>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                type="button"
                                style="secondary"
                                onClick={handleReject}
                                enabled={step !== 'saving'}
                            >
                                Reject &amp; Re-paste
                            </Button>
                            <Button
                                type="button"
                                style="primary"
                                onClick={() => void handleConfirm()}
                                enabled={step !== 'saving'}
                            >
                                {step === 'saving'
                                    ? 'Saving...'
                                    : 'Confirm & Save'}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    )
}
